<template>
  <div class="hx-upload" :class="{ 'is-disabled': disabled }">
    <!-- ==================== 触发区 ==================== -->
     
    <div class="hx-upload__trigger" @click="openDialog">
      <slot name="trigger" :open="openDialog">
        <template v-if="listType === 'picture-card'">
          <div v-if="selectedThumbs.length" class="hx-upload__thumbs">
            <div v-for="(item, idx) in selectedThumbs" :key="idx" class="hx-upload__thumb">
              <img v-if="item.thumbUrl" :src="item.thumbUrl" />
              <div v-else class="hx-upload__thumb-icon"><i :class="item.icon"></i></div>
              <span v-if="!disabled && !multiple" class="hx-upload__thumb-del" @click.stop="handleClear">
                <i class="el-icon-close"></i>
              </span>
              <span v-if="!disabled && multiple" class="hx-upload__thumb-del" @click.stop="removeItem(idx)">
                <i class="el-icon-close"></i>
              </span>
            </div>
            <div v-if="multiple && (!limit || selectedThumbs.length < limit)" class="hx-upload__thumb hx-upload__thumb--add">
              <i class="el-icon-plus"></i>
            </div>
          </div>
          <div v-else class="hx-upload__placeholder">
            <i class="el-icon-plus"></i>
          </div>
        </template>
        <template v-else>
          <div v-if="selectedThumbs.length" class="hx-upload__value-text">
            <i :class="selectedThumbs[0].icon"></i>
            <span class="hx-upload__value-name">{{ selectedThumbs[0].name }}</span>
            <i v-if="!disabled" class="el-icon-circle-close hx-upload__value-clear" @click.stop="handleClear"></i>
          </div>
          <el-button v-else type="primary" icon="el-icon-upload2" :disabled="disabled">{{ buttonText }}</el-button>
        </template>
      </slot>
    </div>
    <div v-if="tip" class="hx-upload__tip">{{ tip }}</div>

    <!-- ==================== 图片预览 ==================== -->
    <el-dialog :visible.sync="previewVisible" append-to-body custom-class="hx-upload__preview" width="auto">
      <img v-if="previewUrl" :src="previewUrl" class="hx-upload__preview-img" />
    </el-dialog>

    <!-- ==================== 文件选择弹窗 ==================== -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      append-to-body
      :close-on-click-modal="false"
      width="960px"
      custom-class="hx-upload__dialog"
      @open="handleDialogOpen"
    >
      <!-- 顶部工具栏 -->
      <div class="hx-upload__bar">
        <div class="hx-upload__bar-left">
          <el-radio-group v-if="type === 0" v-model="query.type" size="small" @change="handleTypeChange">
            <el-radio-button :label="0">全部</el-radio-button>
            <el-radio-button :label="10">图片</el-radio-button>
            <el-radio-button :label="20">视频</el-radio-button>
            <el-radio-button :label="30">文件</el-radio-button>
          </el-radio-group>
          <span v-else class="hx-upload__bar-type">{{ typeName }}</span>
        </div>
        <div class="hx-upload__bar-right">
          <el-input v-model="query.keyword" size="small" clearable placeholder="搜索文件名" style="width: 200px"
            @keyup.enter.native="handleFilterChange" @clear="handleFilterChange" />
          <el-button size="small" type="primary" icon="el-icon-search" @click="handleFilterChange" style="margin-left: 10px">搜索</el-button>
          <el-button size="small" type="success" icon="el-icon-upload2" @click="triggerUpload" :disabled="!canUploadToCurrent">
            上传到当前分类
          </el-button>
          <input ref="fileInput" type="file" class="hx-upload__input" :accept="uploadAccept" :multiple="multiple" @change="handleFileSelected" />
        </div>
      </div>

      <!-- 主体：左树 + 右网格 -->
      <div class="hx-upload__main">
        <!-- 左侧分类列表（一级平铺） -->
        <div class="hx-upload__side">
          <div class="hx-upload__side-head">
            <span class="hx-upload__side-title">分类</span>
            <el-button type="text" icon="el-icon-plus" @click="openCategoryDialog()" title="新建分类"></el-button>
          </div>
          <div v-loading="categoryLoading" class="hx-upload__side-body">
            <!-- 全部 / 未分类 -->
            <div class="hx-upload__side-item" :class="{ 'is-active': activeCategoryId === 0 }" @click="selectCategory(0)">
              <i class="el-icon-folder-opened"></i>
              <span class="hx-upload__side-name">全部文件</span>
            </div>
            <div class="hx-upload__side-item" :class="{ 'is-active': activeCategoryId === -1 }" @click="selectCategory(-1)">
              <i class="el-icon-folder"></i>
              <span class="hx-upload__side-name">未分类</span>
            </div>

            <!-- 自定义分类（一级平铺） -->
            <div
              v-for="item in categoryList"
              :key="item.id"
              class="hx-upload__side-item"
              :class="{ 'is-active': activeCategoryId === item.id }"
              @click="selectCategory(item.id)"
            >
              <i class="el-icon-folder"></i>
              <span class="hx-upload__side-name">{{ item.name }}</span>
              <span class="hx-upload__cat-ops">
                <i class="el-icon-edit" title="编辑" @click.stop="openCategoryDialog(item, true)"></i>
                <i class="el-icon-delete" title="删除" @click.stop="handleDeleteCategory(item)"></i>
              </span>
            </div>

            <!-- 空分类 -->
            <div v-if="!categoryLoading && !categoryList.length" class="hx-upload__side-empty">
              暂无分类<br />点击右上角 + 新建
            </div>
          </div>
        </div>

        <!-- 右侧网格 -->
        <div class="hx-upload__content">
          <div v-loading="loading" class="hx-upload__grid">
            <!-- 上传中临时卡片 -->
            <div v-for="u in uploadingList" :key="u.uid" class="hx-upload__card is-uploading">
              <div class="hx-upload__card-inner">
                <el-progress type="circle" :width="48" :percentage="u.percent" />
                <span class="hx-upload__card-hint">{{ u.hint }}</span>
              </div>
              <div class="hx-upload__card-name" :title="u.name">{{ u.name }}</div>
            </div>

            <!-- 文件卡片 -->
            <div v-for="item in list" :key="item.file_id" class="hx-upload__card"
              :class="{ 'is-selected': isSelected(item) }" @click="toggleSelect(item)">
              <div class="hx-upload__card-inner">
                <img v-if="item.file_type === 10" :src="resolveUrl(item.file_url)" class="hx-upload__card-img" />
                <div v-else class="hx-upload__card-icon"><i :class="getFileIcon(item.file_ext)"></i></div>
                <span v-if="isSelected(item)" class="hx-upload__card-check"><i class="el-icon-check"></i></span>
                <span class="hx-upload__card-actions">
                  <span v-if="item.file_type === 10" class="hx-upload__card-act" title="预览" @click.stop="handlePreview(item)">
                    <i class="el-icon-zoom-in"></i>
                  </span>
                  <span class="hx-upload__card-act is-danger" title="删除" @click.stop="handleDeleteFile(item)">
                    <i class="el-icon-delete"></i>
                  </span>
                </span>
              </div>
              <div class="hx-upload__card-name" :title="item.original_name">{{ item.original_name }}</div>
              <div class="hx-upload__card-size">{{ formatSize(item.file_size) }}</div>
            </div>

            <!-- 空状态 -->
            <div v-if="!loading && !list.length && !uploadingList.length" class="hx-upload__empty">
              <i class="el-icon-folder-opened"></i>
              <p>该分类下暂无文件</p>
            </div>
          </div>

          <!-- 分页 -->
          <div class="hx-upload__pager">
            <el-pagination small background layout="total, prev, pager, next"
              :current-page="query.page" :page-size="query.pageSize" :total="total" @current-change="handlePageChange" />
          </div>
        </div>
      </div>

      <!-- 底部 -->
      <div slot="footer" class="hx-upload__footer">
        <span class="hx-upload__selected-count">
          已选 <b>{{ tempSelected.length }}</b> 个<span v-if="limit">（上限 {{ limit }}）</span>
        </span>
        <div>
          <el-button size="small" @click="dialogVisible = false">取 消</el-button>
          <el-button size="small" type="primary" :disabled="!tempSelected.length" @click="handleConfirm">确 定</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- ==================== 分类新增/编辑弹窗 ==================== -->
    <el-dialog
      :title="categoryDialogTitle"
      :visible.sync="categoryDialogVisible"
      append-to-body
      width="440px"
      custom-class="hx-upload__cat-dialog"
      :close-on-click-modal="false"
    >
      <el-form ref="catForm" :model="catForm" :rules="catRules" label-width="80px" size="medium">
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="catForm.name" placeholder="请输入分类名称" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="catForm.sort" :min="0" :max="9999" />
          <span class="hx-upload__form-tip">越小越靠前</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="categoryDialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="catSubmitLoading" @click="handleCategorySubmit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { Message, MessageBox } from 'element-ui'
import {
  getFilePicker,
  batchDeleteFile,
  getCategoryTree,
  createCategory,
  updateCategory,
  deleteCategory
} from '@/api/upload'
import { uploadFile, createUploadController, ABORT_ERROR_CODE } from '@/utils/uploadFile'
import { formatSize, isImageExt, FILE_TYPE } from '@/utils/fileSize'

let uidSeed = Date.now()
function genUid() {
  return `up_${uidSeed++}`
}

const TYPE_NAMES = { 10: '图片', 20: '视频', 30: '文件' }
const TYPE_ACCEPTS = { 10: 'image/*', 20: 'video/*', 30: '' }

export default {
  name: 'HxUpload',

  model: { prop: 'value', event: 'change' },

  props: {
    value: { type: [Object, Array], default: null },
    // 限定文件类型：0全部 10图片 20视频 30文件
    type: { type: Number, default: 0 },
    multiple: { type: Boolean, default: false },
    limit: { type: Number, default: 0 },
    maxSize: { type: Number, default: 0 },
    categoryId: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    listType: { type: String, default: 'picture-card' },
    buttonText: { type: String, default: '点击上传' },
    accept: { type: String, default: '' },
    tip: { type: String, default: '' },
    pageSize: { type: Number, default: 24 }
  },

  data() {
    return {
      // 主弹窗
      dialogVisible: false,
      loading: false,
      list: [],
      total: 0,
      query: { type: 0, keyword: '', page: 1, pageSize: this.pageSize },
      tempSelected: [],
      uploadingList: [],
      previewVisible: false,
      previewUrl: '',

      // 分类列表（一级平铺）
      activeCategoryId: 0,
      categoryList: [],
      categoryLoading: false,

      // 分类新增/编辑
      categoryDialogVisible: false,
      categoryDialogTitle: '新建分类',
      catSubmitLoading: false,
      catForm: {
        id: null,
        name: '',
        sort: 0
      },
      catRules: {
        name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 1, max: 20, message: '长度在 1-20 个字符', trigger: 'blur' }
        ]
      }
    }
  },

  computed: {
    dialogTitle() {
      return this.type ? `选择${TYPE_NAMES[this.type]}` : '选择文件'
    },
    typeName() {
      return TYPE_NAMES[this.type] || '文件'
    },
    uploadAccept() {
      return this.accept || TYPE_ACCEPTS[this.type] || ''
    },
    selectedThumbs() {
      const arr = Array.isArray(this.value) ? this.value : (this.value ? [this.value] : [])
      return arr.map(item => ({
        thumbUrl: (item.file_type === undefined || item.file_type === FILE_TYPE.IMAGE) ? this.resolveUrl(item.file_url) : '',
        icon: this.getFileIcon(item.file_ext),
        name: item.original_name || ''
      }))
    },
    // 上传按钮是否可用：未分类(-1) 不允许上传
    canUploadToCurrent() {
      return this.activeCategoryId !== -1
    }
  },

  methods: {
    // ==================== 主弹窗 ====================

    openDialog() {
      if (this.disabled) return
      this.dialogVisible = true
    },

    handleDialogOpen() {
      this.query.type = this.type
      this.query.keyword = ''
      this.query.page = 1
      this.activeCategoryId = this.categoryId || 0
      this.tempSelected = this.cloneSelected()
      this.fetchCategoryList()
      this.fetchList()
    },

    cloneSelected() {
      const arr = Array.isArray(this.value) ? this.value : (this.value ? [this.value] : [])
      return arr.map(item => ({ ...item }))
    },

    // ==================== 文件列表 ====================

    async fetchList() {
      this.loading = true
      try {
        const res = await getFilePicker({
          page: this.query.page,
          pageSize: this.query.pageSize,
          file_type: this.query.type,
          category_id: this.activeCategoryId,
          keyword: this.query.keyword
        })
        this.list = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.loading = false
      }
    },

    handleFilterChange() {
      this.query.page = 1
      this.fetchList()
    },

    handleTypeChange() {
      this.query.page = 1
      // 类型变化时刷新分类列表（按类型筛选分类）
      this.fetchCategoryList()
      this.fetchList()
    },

    handlePageChange(p) {
      this.query.page = p
      this.fetchList()
    },

    // ==================== 选择 ====================

    isSelected(item) {
      return this.tempSelected.some(s => s.file_id === item.file_id)
    },

    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.tempSelected = this.tempSelected.filter(s => s.file_id !== item.file_id)
        return
      }
      if (this.multiple) {
        if (this.limit > 0 && this.tempSelected.length >= this.limit) {
          Message.warning(`最多只能选择 ${this.limit} 个文件`)
          return
        }
        this.tempSelected.push({ ...item })
      } else {
        this.tempSelected = [{ ...item }]
      }
    },

    handleConfirm() {
      if (!this.tempSelected.length) return
      const payload = this.multiple ? this.tempSelected : this.tempSelected[0]
      this.$emit('change', payload)
      this.$emit('select', payload)
      this.dialogVisible = false
    },

    handleClear() {
      const payload = this.multiple ? [] : null
      this.$emit('change', payload)
    },

    removeItem(idx) {
      const arr = Array.isArray(this.value) ? [...this.value] : []
      arr.splice(idx, 1)
      this.$emit('change', arr)
    },

    // ==================== 分类列表 ====================

    async fetchCategoryList() {
      this.categoryLoading = true
      try {
        const res = await getCategoryTree()
        // 接口返回一级平铺数组，兼容历史树形结构（拍平）
        this.categoryList = this.flattenCategory(res.data) || []
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.categoryLoading = false
      }
    },

    // 拍平历史树形返回（保险措施，确保始终是扁平数组）
    flattenCategory(list) {
      const result = []
      const walk = (nodes) => {
        if (!Array.isArray(nodes)) return
        nodes.forEach(n => {
          result.push({ id: n.id, name: n.name, sort: n.sort })
          if (n.children && n.children.length) walk(n.children)
        })
      }
      walk(list)
      return result
    },

    selectCategory(id) {
      if (this.activeCategoryId === id) return
      this.activeCategoryId = id
      this.query.page = 1
      this.fetchList()
    },

    // ==================== 分类 CRUD ====================

    openCategoryDialog(row, isEdit = false) {
      if (isEdit) {
        // 编辑
        this.categoryDialogTitle = '编辑分类'
        this.catForm = {
          id: row.id,
          name: row.name,
          sort: row.sort || 0
        }
      } else {
        // 新建
        this.categoryDialogTitle = '新建分类'
        this.catForm = {
          id: null,
          name: '',
          sort: 0
        }
      }
      this.categoryDialogVisible = true
      this.$nextTick(() => {
        this.$refs.catForm && this.$refs.catForm.clearValidate()
      })
    },

    async handleCategorySubmit() {
      try {
        await this.$refs.catForm.validate()
      } catch (e) {
        return
      }

      this.catSubmitLoading = true
      try {
        if (this.catForm.id) {
          // 编辑
          await updateCategory({
            id: this.catForm.id,
            name: this.catForm.name,
            sort: this.catForm.sort
          })
          Message.success('编辑成功')
        } else {
          // 新建
          await createCategory({
            name: this.catForm.name,
            sort: this.catForm.sort
          })
          Message.success('创建成功')
        }
        this.categoryDialogVisible = false
        await this.fetchCategoryList()
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.catSubmitLoading = false
      }
    },

    handleDeleteCategory(row) {
      MessageBox.confirm(
        `确认删除分类「${row.name}」吗？<br/>如果该分类下有文件将无法删除。`,
        '删除分类',
        {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning',
          dangerouslyUseHTMLString: true
        }
      ).then(async () => {
        try {
          await deleteCategory(row.id)
          Message.success('删除成功')
          // 如果删除的是当前选中的分类，回退到全部
          if (this.activeCategoryId === row.id) {
            this.activeCategoryId = 0
            this.query.page = 1
          }
          await this.fetchCategoryList()
          this.fetchList()
        } catch (e) {
          // 拦截器已提示
        }
      }).catch(() => { /* 用户取消，静默 */ })
    },

    // ==================== 上传 ====================

    triggerUpload() {
      if (!this.canUploadToCurrent) {
        Message.warning('「未分类」下不能直接上传，请选择具体分类')
        return
      }
      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    handleFileSelected(e) {
      const files = Array.from(e.target.files || [])
      this.$refs.fileInput.value = ''
      if (!files.length) return
      // 上传到媒体库始终支持多选；是否自动选中由 multiple 控制（在 uploadOne 内处理）
      files.forEach(file => this.uploadOne(file))
    },

    async uploadOne(file) {
      if (this.maxSize > 0 && file.size > this.maxSize) {
        Message.error(`「${file.name}」超过大小限制（${formatSize(this.maxSize)}）`)
        return
      }
      if (file.size === 0) {
        Message.error(`「${file.name}」为空文件`)
        return
      }

      const item = {
        uid: genUid(),
        name: file.name,
        percent: 0,
        hint: '准备中...',
        controller: createUploadController()
      }
      this.uploadingList.push(item)

      try {
        const fileInfo = await uploadFile(file, {
          // 上传到当前选中分类（未分类场景上面已拦截，这里 activeCategoryId > 0 或 = 0）
          categoryId: this.activeCategoryId > 0 ? this.activeCategoryId : 0,
          signal: item.controller.signal,
          onProgress: (percent, info) => {
            item.percent = percent
            item.hint = info.mode === 'hashing'
              ? '计算指纹...'
              : info.mode === 'instant'
                ? '秒传中'
                : info.mode === 'multipart'
                  ? `分片 ${info.currentPart}/${info.totalParts}`
                  : '上传中'
          }
        })

        if (this.multiple) {
          if (this.limit > 0 && this.tempSelected.length >= this.limit) {
            Message.warning('已达选择上限，已上传但未选中')
          } else {
            this.tempSelected.push({ ...fileInfo })
          }
        } else {
          this.tempSelected = [{ ...fileInfo }]
        }
        Message.success(`「${file.name}」上传成功`)
        this.fetchList()
      } catch (err) {
        if (err && err.code === ABORT_ERROR_CODE) {
          // 取消，静默
        } else {
          Message.error(`「${file.name}」上传失败：${(err && err.message) || ''}`)
        }
      } finally {
        const idx = this.uploadingList.findIndex(u => u.uid === item.uid)
        if (idx > -1) this.uploadingList.splice(idx, 1)
      }
    },

    // ==================== 预览 / 删除文件 ====================

    handlePreview(item) {
      const url = this.resolveUrl(item.file_url)
      if (!url) return
      this.previewUrl = url
      this.previewVisible = true
    },

    handleDeleteFile(item) {
      MessageBox.confirm(
        `确认删除文件「${item.original_name}」吗？删除后不可恢复。`,
        '删除确认',
        { confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning' }
      ).then(async () => {
        try {
          await batchDeleteFile([item.file_id])
          Message.success('删除成功')
          this.tempSelected = this.tempSelected.filter(s => s.file_id !== item.file_id)
          this.refreshAfterDelete()
        } catch (e) {
          // 拦截器已提示
        }
      }).catch(() => { /* 用户取消，静默 */ })
    },

    refreshAfterDelete() {
      const remain = this.total - 1
      const maxPage = Math.max(1, Math.ceil(remain / this.query.pageSize))
      if (this.query.page > maxPage) this.query.page = maxPage
      this.fetchList()
    },

    // ==================== 工具 ====================

    resolveUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      const base = process.env.VUE_APP_BASE_API || ''
      return base + (url.startsWith('/') ? url : '/' + url)
    },

    getFileIcon(ext) {
      const e = (ext || '').toLowerCase()
      if (isImageExt(e)) return 'el-icon-picture-outline'
      if (['mp4', 'mov', 'avi', 'mkv', 'flv', 'webm'].includes(e)) return 'el-icon-video-camera'
      if (['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(e)) return 'el-icon-headset'
      if (['pdf'].includes(e)) return 'el-icon-document'
      if (['doc', 'docx'].includes(e)) return 'el-icon-document'
      if (['xls', 'xlsx'].includes(e)) return 'el-icon-tickets'
      if (['ppt', 'pptx'].includes(e)) return 'el-icon-files'
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(e)) return 'el-icon-folder-opened'
      return 'el-icon-files'
    },

    formatSize
  }
}
</script>

<style lang="less" scoped>
// ==================== 暗色主题变量（与 element.less 保持一致）====================
@primary: #00D4C8;
@success: #36CFC9;
@warning: #FFA940;
@danger: #FF6B6B;
@border: rgba(255, 255, 255, 0.15);
@text-regular: rgba(250, 250, 250, 0.85);
@text-secondary: rgba(250, 250, 250, 0.7);
@text-muted: rgba(250, 250, 250, 0.6);
@panel-bg: #283363;
@card-bg: #1A2342;
@hover-bg: rgba(0, 212, 200, 0.12);

.hx-upload {
  width: 100%;
  line-height: 1.5;
  &.is-disabled { cursor: not-allowed; opacity: 0.5; }
}

/* ==================== 触发区 ==================== */
.hx-upload__trigger { cursor: pointer; display: inline-block; }

// picture-card 占位框
.hx-upload__placeholder {
  width: 100px; height: 100px;
  border: 1px dashed @border; border-radius: 6px;
  background-color: @panel-bg;
  display: flex; align-items: center; justify-content: center;
  font-size: 28px; color: @text-muted;
  box-sizing: border-box;
  transition: border-color 0.2s, color 0.2s;
  &:hover { border-color: @primary; color: @primary; }
}

// 已选缩略图
.hx-upload__thumbs { display: flex; flex-wrap: wrap; gap: 8px; }
.hx-upload__thumb {
  position: relative; width: 100px; height: 100px;
  border-radius: 6px; border: 1px solid @border;
  overflow: hidden; background: @card-bg; box-sizing: border-box;
  img { width: 100%; height: 100%; object-fit: cover; }
  &--add {
    border-style: dashed; display: flex; align-items: center; justify-content: center;
    font-size: 28px; color: @text-muted; background: @panel-bg;
    &:hover { border-color: @primary; color: @primary; }
  }
}
.hx-upload__thumb-icon {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-size: 32px; color: @text-secondary;
}
.hx-upload__thumb-del {
  position: absolute; top: 2px; right: 2px;
  width: 18px; height: 18px; border-radius: 50%;
  background: rgba(0,0,0,.6); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; cursor: pointer; opacity: 0;
  transition: opacity 0.2s;
}
.hx-upload__thumb:hover .hx-upload__thumb-del { opacity: 1; }

// text 风格触发器
.hx-upload__value-text {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px; border: 1px solid @border; border-radius: 4px;
  background: @panel-bg; font-size: 13px; color: @text-regular; max-width: 100%;
  &:hover { border-color: @primary; }
}
.hx-upload__value-name {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px;
}
.hx-upload__value-clear { color: @text-muted; cursor: pointer; &:hover { color: @danger; } }

.hx-upload__input { display: none; }
.hx-upload__tip { margin-top: 6px; font-size: 12px; color: @text-secondary; line-height: 1.6; }

/* ==================== 弹窗工具栏 ==================== */
.hx-upload__bar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; flex-wrap: wrap; gap: 8px;
}
.hx-upload__bar-type { font-size: 14px; font-weight: 500; color: @text-regular; }
.hx-upload__bar-right { display: flex; align-items: center; }

/* ==================== 主体：左树右网格 ==================== */
.hx-upload__main {
  display: flex;
  border: 1px solid @border;
  border-radius: 4px;
  height: 480px;
  overflow: hidden;
}

/* 左侧分类 */
.hx-upload__side {
  width: 200px;
  flex-shrink: 0;
  border-right: 1px solid @border;
  display: flex;
  flex-direction: column;
  background: fade(@panel-bg, 50%);
}
.hx-upload__side-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid @border;
}
.hx-upload__side-title { font-size: 13px; font-weight: 500; color: @text-regular; }
.hx-upload__side-body { flex: 1; overflow-y: auto; padding: 4px 0; }

// 固定项（全部文件 / 未分类）
.hx-upload__side-item {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 12px; cursor: pointer; font-size: 13px;
  color: @text-regular;
  transition: background-color 0.2s;
  &:hover { background: @hover-bg; }
  &.is-active {
    background: fade(@primary, 15%);
    color: @primary;
    font-weight: 500;
  }
  i { color: @text-secondary; }
  &.is-active i { color: @primary; }
}
.hx-upload__side-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.hx-upload__side-empty {
  padding: 30px 12px; text-align: center;
  font-size: 12px; color: @text-muted; line-height: 1.8;
}

/* 分类项悬浮操作（编辑/删除，仅自定义分类用） */
.hx-upload__cat-ops {
  display: none;
  gap: 6px;
  margin-left: auto;
  i {
    color: @text-secondary; cursor: pointer; font-size: 13px;
    &:hover { color: @primary; }
    &.el-icon-delete:hover { color: @danger; }
  }
}
.hx-upload__side-item:hover .hx-upload__cat-ops { display: flex; }
// 选中态下图标也跟随主色
.hx-upload__side-item.is-active .hx-upload__cat-ops i { color: @text-secondary; }

/* 右侧内容 */
.hx-upload__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hx-upload__grid {
  flex: 1;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  // 关键：start 让卡片按内容高度排列，避免 Grid 默认 stretch 把卡片拉成等高
  // 否则文件名长短不一的卡片底部会有空白，点击空白会误触发选中
  align-items: start;
  gap: 12px;
  padding: 12px;
}

/* 文件卡片 */
.hx-upload__card {
  cursor: pointer; user-select: none;
  // 关键：强制卡片在 grid 单元格内顶部对齐 + 高度紧贴内容
  // 避免 grid 默认 stretch 把卡片拉伸成等高，导致底部出现可误点击的空白
  align-self: start;
  justify-self: stretch;
  &-inner {
    position: relative; width: 100%; aspect-ratio: 1/1;
    border: 2px solid @border; border-radius: 6px;
    overflow: hidden; background: @card-bg;
    display: flex; align-items: center; justify-content: center;
    box-sizing: border-box; transition: border-color 0.2s;
    &:hover { border-color: @primary; }
  }
  &.is-selected .hx-upload__card-inner {
    border-color: @primary;
    box-shadow: 0 0 0 2px fade(@primary, 30%);
  }
  &-img { width: 100%; height: 100%; object-fit: cover; }
  &-icon { font-size: 40px; color: @text-secondary; }
  &-name {
    margin-top: 6px; font-size: 12px; color: @text-regular;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: center;
  }
  &-size { font-size: 11px; color: @text-secondary; text-align: center; }
  // 选中角标
  &-check {
    position: absolute; top: 0; right: 0;
    width: 22px; height: 22px;
    background: @primary; color: #001a1a;
    display: flex; align-items: center; justify-content: center;
    border-radius: 0 0 0 6px; font-size: 14px;
  }
  // 悬浮操作遮罩
  &-actions {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(1, 30, 74, 0.55); opacity: 0; transition: opacity 0.2s;
  }
  &:hover &-actions { opacity: 1; }
  &-act {
    color: #fff; font-size: 20px; cursor: pointer; margin: 0 4px;
    &:hover { color: @primary; }
    &.is-danger:hover { color: @danger; }
  }
  // 上传中卡片
  &.is-uploading {
    cursor: default;
    .hx-upload__card-inner { border-style: dashed; flex-direction: column; gap: 6px; }
  }
  &-hint { font-size: 12px; color: @text-secondary; }
}

/* 空状态 */
.hx-upload__empty {
  grid-column: 1 / -1;
  padding: 60px 0; text-align: center; color: @text-muted;
  .el-icon-folder-opened { font-size: 48px; color: fade(@text-secondary, 40%); }
  p { margin: 12px 0 0; font-size: 13px; }
}

.hx-upload__pager { padding: 8px 12px; text-align: right; border-top: 1px solid @border; }

.hx-upload__footer { display: flex; align-items: center; justify-content: space-between; }
.hx-upload__selected-count {
  font-size: 13px; color: @text-secondary;
  b { color: @primary; font-size: 15px; margin: 0 2px; }
}

/* 分类表单提示 */
.hx-upload__form-tip { margin-left: 8px; font-size: 12px; color: @text-secondary; }
</style>

<style lang="less">
// 非 scoped：覆盖弹窗内 append-to-body 挂载的 element 组件样式
// 主题色与 element.less 保持一致
@primary: #00D4C8;
@border: rgba(255, 255, 255, 0.15);
@text-secondary: rgba(250, 250, 250, 0.7);
@hover-bg: rgba(0, 212, 200, 0.12);

/* 预览弹窗 */
.hx-upload__preview {
  .el-dialog__header { padding: 10px; }
  .el-dialog__body { text-align: center; padding: 10px; }
  .hx-upload__preview-img { max-width: 80vw; max-height: 75vh; object-fit: contain; }
}

/* 选择弹窗：内部 loading 适配 */
.hx-upload__dialog {
  .el-dialog__body { padding: 16px 20px; }
  // loading 遮罩用深色
  .el-loading-mask { background-color: rgba(1, 30, 74, 0.5); }
  .el-loading-spinner .circular { stroke: @primary; }
  .el-loading-spinner .el-loading-text { color: @text-secondary; }
}

/* 分类弹窗 */
.hx-upload__cat-dialog {
  .el-dialog__body { padding: 20px 20px 0; }
}
</style>
